import { Shield } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatUSDT } from "@/lib/mockData";
import { C } from "./profileTheme";
import { useTranslation } from "react-i18next";
import { useGetUserAutoTradeLossCompPool2Records } from "@/hooks/api/useGetUserAutoTradeLossCompPool2Records";
import {
  AutoTradeLossCompPool2RecordStatus,
  type AutoTradeLossCompPool2RecordItem,
} from "@/api/types";
import { formatEtherWithDecimals, formatKMBTruncate } from "@/lib/utils";
import { getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS } from "@/config/env";
import dayjs from "dayjs";

function formatTskAmountWei(tskAmount: number | string | undefined): string {
  const raw = tskAmount ?? "0";
  const num = Number(formatEtherWithDecimals(String(raw)));
  if (!Number.isFinite(num) || num === 0) return "0";
  return formatKMBTruncate(num);
}

function recordKey(item: AutoTradeLossCompPool2RecordItem, index: number) {
  return `${item.compDate}-${item.createTime}-${index}`;
}

export function LossCompensationHistoryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tokenTsk = getTokenName(CCC_AIF_ADDRESS);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGetUserAutoTradeLossCompPool2Records(open);

  const records = data?.pages.flatMap(page => page.records ?? []) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 48 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void fetchNextPage();
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent
        className="max-w-sm"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.amber }}>
            {t("profilePage.lossCompensationHistoryDialog.title")}
          </DialogTitle>
        </DialogHeader>
        <div
          className="rounded-lg p-3 mb-3"
          style={{
            background: "rgba(245,158,11,0.06)",
            border: "1px solid rgba(245,158,11,0.15)",
          }}
        >
          <div className="text-xs mb-1" style={{ color: C.dim }}>
            {t("profilePage.lossCompensationHistoryDialog.rulesTitle")}
          </div>
          <div className="text-xs leading-relaxed" style={{ color: C.text }}>
            {t("profilePage.lossCompensationHistoryDialog.rulesBody")}
          </div>
        </div>
        <div
          className="space-y-2 overflow-y-auto pr-1"
          style={{ maxHeight: "min(60vh, 400px)" }}
          onScroll={handleScroll}
        >
          {isLoading ? (
            <div className="text-center py-8 text-xs" style={{ color: C.dim }}>
              {t("profilePage.lossCompensationHistoryDialog.loading")}
            </div>
          ) : records.length > 0 ? (
            records.map((lc, index) => {
              const isIssued =
                lc.status === AutoTradeLossCompPool2RecordStatus.Issued;
              return (
                <div
                  key={recordKey(lc, index)}
                  className="rounded-lg px-3 py-2"
                  style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Shield
                        size={12}
                        className="shrink-0"
                        style={{ color: C.amber }}
                      />
                      <span className="text-xs font-semibold shrink-0">
                        {t(
                          "profilePage.lossCompensationHistoryDialog.weekLabel",
                          {
                            n: index + 1,
                          }
                        )}
                      </span>
                      <span
                        className="text-[9px] mono-num truncate"
                        style={{ color: C.dim }}
                      >
                        {lc.createTime
                          ? dayjs(lc.createTime).format("YYYY-MM-DD HH:mm")
                          : ""}
                      </span>
                    </div>
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded shrink-0 ml-1"
                      style={{
                        background: isIssued
                          ? "rgba(34,197,94,0.08)"
                          : "rgba(245,158,11,0.10)",
                        color: isIssued ? C.green : C.amber,
                      }}
                    >
                      {isIssued
                        ? t(
                            "profilePage.lossCompensationHistoryDialog.statusIssued"
                          )
                        : t(
                            "profilePage.lossCompensationHistoryDialog.statusDistributing"
                          )}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                    <div className="flex justify-between">
                      <span style={{ color: C.dim }}>
                        {t(
                          "profilePage.lossCompensationHistoryDialog.pendingAmount"
                        )}
                      </span>
                      <span className="mono-num" style={{ color: C.amber }}>
                        {formatUSDT(lc.pendingCompUsdt)} U
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: C.dim }}>
                        {t(
                          "profilePage.lossCompensationHistoryDialog.compensationRate"
                        )}
                      </span>
                      <span className="mono-num" style={{ color: C.text }}>
                        {Number(lc.compRatePercent ?? 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between col-span-2">
                      <span style={{ color: C.dim }}>
                        {t(
                          "profilePage.lossCompensationHistoryDialog.tskReceived",
                          {
                            tokenName: tokenTsk,
                          }
                        )}
                      </span>
                      <span
                        className="mono-num font-medium text-right"
                        style={{ color: C.green }}
                      >
                        +{formatTskAmountWei(lc.tskAmount)} {tokenTsk} (≈$
                        {formatUSDT(lc.tskValueUsdt)})
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-xs" style={{ color: C.dim }}>
              {t("profilePage.lossCompensationHistoryDialog.empty")}
            </div>
          )}
          {isFetchingNextPage && (
            <div
              className="text-center py-2 text-[10px]"
              style={{ color: C.dim }}
            >
              {t("profilePage.lossCompensationHistoryDialog.loading")}
            </div>
          )}
        </div>
        {total > 0 && (
          <div
            className="text-center text-[10px] pt-2"
            style={{ color: C.dim }}
          >
            {t("profilePage.lossCompensationHistoryDialog.totalCount", {
              count: total,
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
